import { Avatar, Box, Flex, Image, Text } from "@chakra-ui/react";
import { FeedCardHalo } from "./FeedCardHalo";
import { FeedTopCommentEntry, FeedTopCommentItem } from "./types";
import { useOpenFeedCommentsMenu } from "./hooks/useOpenFeedCommentsMenu";

interface FeedTopCommentClusterProps {
  item: FeedTopCommentItem;
  isActive: boolean;
}

/**
 * "Readers are saying" cluster — one fullscreen slot composed of N quote
 * rows. Each row is its own tap target into the slide-in CommentsMenu for
 * the release the quote was posted on.
 */
export function FeedTopCommentCluster({
  item,
  isActive,
}: FeedTopCommentClusterProps) {
  const heroSrc =
    item.comments[0]?.comic.cover_url ??
    item.comments[0]?.comic.cover_small_url ??
    null;

  return (
    <Box position="relative" w="100%" h="100%">
      <FeedCardHalo src={heroSrc} />
      <Box
        position="relative"
        zIndex={1}
        w="100%"
        h="100%"
        bgGradient="linear(135deg, rgba(28, 38, 60, 0.95) 0%, rgba(14, 16, 24, 0.95) 100%)"
        borderRadius="2xl"
        border="1px solid rgba(255,255,255,0.06)"
        color="white"
        overflow="hidden"
        p={3}
      >
        <Flex direction="column" gap={3} h="100%">
          {item.comments.map((c) => (
            <CommentRow
              key={c.comment_id}
              entry={c}
              isActive={isActive}
            />
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

function CommentRow({
  entry,
  isActive,
}: {
  entry: FeedTopCommentEntry;
  isActive: boolean;
}) {
  const cover = entry.comic.cover_url ?? entry.comic.cover_small_url ?? null;
  const openCommentsMenu = useOpenFeedCommentsMenu();

  return (
    <Flex
      as="a"
      href={entry.comic.url}
      // Primary tap → CommentsMenu for this entry's release. Right/cmd-click
      // routes to the comic page via the href.
      onClick={(e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        openCommentsMenu(entry.release_uuid);
      }}
      tabIndex={isActive ? 0 : -1}
      direction="row"
      flex="1"
      minH="0"
      bg="rgba(20, 28, 44, 0.6)"
      borderRadius="xl"
      overflow="hidden"
      cursor="pointer"
      _hover={{ bg: "rgba(28, 40, 64, 0.7)" }}
      transition="background 120ms ease"
    >
      <Box position="relative" flexShrink={0} w="32%" maxW="140px" bg="black">
        {cover ? (
          <Image
            src={cover}
            alt={entry.comic.name}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        ) : (
          <Box
            w="100%"
            h="100%"
            bgGradient="linear(135deg, cyan.400 0%, blue.700 100%)"
          />
        )}
        <Box
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          h="55%"
          bgGradient="linear(to-t, rgba(0,0,0,0.92) 0%, transparent 100%)"
          pointerEvents="none"
        />
        <Box position="absolute" left={2} right={2} bottom={2}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            noOfLines={2}
            lineHeight="1.2"
            sx={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}
          >
            {entry.comic.name}
          </Text>
        </Box>
      </Box>
      <Flex direction="column" flex="1" minW="0" px={3} py={2} gap={1}>
        <Flex align="center" gap={2} minW="0">
          <Avatar
            size="xs"
            src={entry.author_avatar_url ?? undefined}
            name={entry.author_username}
            bg="cyan.500"
            color="white"
            flexShrink={0}
          />
          <Text fontSize="sm" fontWeight="bold" noOfLines={1} flex="1" minW="0">
            @{entry.author_username}
          </Text>
          {entry.total_good > 0 ? (
            <Text fontSize="xs" opacity={0.7} flexShrink={0}>
              ▲ {entry.total_good}
            </Text>
          ) : null}
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="medium"
          lineHeight="1.45"
          noOfLines={3}
          flex="1"
          minH="0"
        >
          <Box
            as="span"
            color="cyan.300"
            fontWeight="bold"
            mr="3px"
          >
            {"“"}
          </Box>
          {entry.body}
          <Box
            as="span"
            color="cyan.300"
            fontWeight="bold"
            ml="2px"
          >
            {"”"}
          </Box>
        </Text>
      </Flex>
    </Flex>
  );
}
